if (!customElements.get('coffee-vendor-carousel')) {
  customElements.define('coffee-vendor-carousel', class extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.coffee-vendor__grid');
      this.controls = this.querySelector('.coffee-vendor__controls');
      this.previous = this.querySelector('[data-vendor-previous]');
      this.next = this.querySelector('[data-vendor-next]');
      if (!this.list || !this.controls) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.list.addEventListener('scroll', () => this.scheduleUpdate(), { ...options, passive: true });
      this.list.addEventListener('keydown', event => {
        if (event.target !== this.list || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') {
          this.list.scrollTo({ left: event.key === 'Home' ? 0 : this.list.scrollWidth, behavior: 'instant' });
        } else {
          this.move(event.key === 'ArrowRight' ? 1 : -1, true);
        }
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this.resizeObserver.observe(this.list);
      this.update();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }
    scheduleUpdate() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }
    update() {
      const end = this.list.scrollWidth - this.list.clientWidth;
      const overflow = end > 2;
      this.controls.hidden = !overflow;
      this.list.toggleAttribute('data-overflow', overflow);
      if (overflow) this.list.setAttribute('tabindex', '0');
      else this.list.removeAttribute('tabindex');
      this.previous.disabled = !overflow || this.list.scrollLeft <= 2;
      this.next.disabled = !overflow || this.list.scrollLeft >= end - 2;
    }
    move(direction, keyboard = false) {
      const card = this.list.firstElementChild;
      if (!card) return;
      const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.list).columnGap || 0);
      const distance = keyboard ? step : Math.max(1, Math.round(this.list.clientWidth / step)) * step;
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.list.scrollBy({ left: direction * distance, behavior: keyboard || reducedMotion ? 'instant' : 'smooth' });
    }
  });
}

// "More from <brand>" cards: the variant select drawn as the coffee page's dose picker (a text
// trigger with a chevron beside it, a white menu of ticked options). The select stays the form's
// field. The menu sits on <body> so the cards' horizontal scroller doesn't clip it.
if (!customElements.get('vendor-option-picker')) {
  let vendorPickerCount = 0;
  customElements.define('vendor-option-picker', class extends HTMLElement {
    connectedCallback() {
      if (this.trigger) {
        document.body.append(this.menu);
        return;
      }
      const select = this.querySelector('select');
      if (!select || !select.options.length) return;
      this.select = select;
      const id = `vendor-option-picker-${++vendorPickerCount}`;
      const labelText = this.querySelector('label')?.textContent.trim() || 'Choose an option';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'coffee-vendor__picker-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', `${id}-menu`);
      const value = document.createElement('span');
      value.className = 'coffee-vendor__picker-value';
      const chevron = document.createElement('span');
      chevron.className = 'coffee-vendor__picker-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      trigger.append(value, chevron);

      const menu = document.createElement('div');
      menu.id = `${id}-menu`;
      menu.className = 'coffee-vendor-menu';
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-label', labelText);
      menu.hidden = true;

      const card = this.closest('.coffee-vendor__card');
      const priceLabel = card?.querySelector('.price__regular');
      const show = (option) => {
        value.textContent = option.dataset.title || option.textContent;
        trigger.setAttribute('aria-label', `${labelText}: ${value.textContent}`);
        if (priceLabel && option.dataset.price) priceLabel.textContent = option.dataset.price;
      };

      this.options = [...select.options].map((source) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'coffee-vendor-menu__option';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', String(source.selected));
        option.tabIndex = -1;
        const tick = document.createElement('span');
        tick.className = 'coffee-vendor-menu__tick';
        tick.setAttribute('aria-hidden', 'true');
        tick.textContent = '✓';
        const name = document.createElement('span');
        name.className = 'coffee-vendor-menu__name';
        name.textContent = source.dataset.title || source.textContent;
        option.append(tick, name);
        if (source.dataset.price) {
          const price = document.createElement('span');
          price.className = 'coffee-vendor-menu__price';
          price.textContent = source.dataset.price;
          option.append(price);
        }
        option.addEventListener('click', () => {
          select.value = source.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          this.options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
          show(source);
          this.close(true);
        });
        menu.append(option);
        return option;
      });

      this.outside = (event) => {
        if (!this.contains(event.target) && !menu.contains(event.target)) this.close();
      };
      // Scrolling the page or the cards moves the menu with its trigger (phones also fire these
      // as the browser's toolbar shows and hides); it closes once the trigger leaves the screen
      this.reposition = (event) => {
        if (event?.type === 'scroll' && menu.contains(event.target)) return;
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.place());
      };
      trigger.addEventListener('click', () => (menu.hidden ? this.open() : this.close()));
      const keys = (event) => {
        const index = this.options.indexOf(document.activeElement);
        if (event.key === 'Escape' && !menu.hidden) {
          event.preventDefault();
          event.stopPropagation();
          this.close(true);
        } else if (event.key === 'Tab' && !menu.hidden) {
          event.preventDefault();
          this.close(true);
        } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          if (event.key === 'Home') this.open(0);
          else if (event.key === 'End') this.open(this.options.length - 1);
          else if (menu.hidden) this.open();
          else {
            const next = index + (event.key === 'ArrowDown' ? 1 : -1);
            if (next >= 0 && next < this.options.length) this.options[next].focus();
          }
        }
      };
      trigger.addEventListener('keydown', keys);
      menu.addEventListener('keydown', keys);

      this.trigger = trigger;
      this.menu = menu;
      show(select.selectedOptions[0] || select.options[0]);
      select.hidden = true;
      select.tabIndex = -1;
      this.append(trigger);
      document.body.append(menu);
    }
    disconnectedCallback() {
      this.close?.();
      this.menu?.remove();
    }
    open(index = this.options.findIndex((item) => item.getAttribute('aria-selected') === 'true')) {
      const { menu, trigger } = this;
      if (menu.hidden) {
        menu.hidden = false;
        this.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        this.place();
        document.addEventListener('pointerdown', this.outside);
        document.addEventListener('focusin', this.outside);
        window.addEventListener('scroll', this.reposition, true);
        window.addEventListener('resize', this.reposition);
      }
      this.options[Math.max(0, index)]?.focus();
    }
    place() {
      const { menu, trigger } = this;
      if (menu.hidden) return;
      const rect = trigger.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth) {
        this.close();
        return;
      }
      const width = Math.min(Math.max(rect.width, 220), window.innerWidth - 32);
      const left = Math.min(Math.max(16, rect.left), window.innerWidth - width - 16);
      menu.style.minWidth = `${width}px`;
      menu.style.left = `${left}px`;
      const height = menu.offsetHeight;
      const below = window.innerHeight - rect.bottom;
      const top = below < height + 16 && rect.top > below ? rect.top - height - 8 : rect.bottom + 8;
      menu.style.top = `${Math.max(8, top)}px`;
    }
    close(restoreFocus = false) {
      if (!this.menu || this.menu.hidden) return;
      this.menu.hidden = true;
      this.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', this.outside);
      document.removeEventListener('focusin', this.outside);
      window.removeEventListener('scroll', this.reposition, true);
      window.removeEventListener('resize', this.reposition);
      cancelAnimationFrame(this.frame);
      if (restoreFocus) this.trigger.focus();
    }
  });
}
